import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import type { Gang, Worker } from "../lib/types";

const TYPE_LABEL: Record<string, string> = {
  HARVESTER: "Harvester",
  CARRIER: "Carrier",
  MANDOR: "Mandor",
  SUPERVISOR: "Supervisor",
  DRIVER: "Driver",
  GENERAL: "General",
};

/** Manpower — worker roster + gang directory. Add/edit later. */
export function Manpower() {
  const workersQ = useQuery({
    queryKey: ["workers"],
    queryFn: () => api.list<Worker>("/v1/workers?limit=500"),
  });
  const gangsQ = useQuery({
    queryKey: ["gangs"],
    queryFn: () => api.list<Gang>("/v1/gangs"),
  });

  const workers = workersQ.data?.data ?? [];
  const gangs = gangsQ.data?.data ?? [];
  const workerById = useMemo(() => {
    const m = new Map<string, Worker>();
    for (const w of workers) m.set(w.id, w);
    return m;
  }, [workers]);

  return (
    <div className="space-y-6 text-sm">
      <div>
        <h1 className="text-lg font-medium">Manpower</h1>
        <p className="text-xs text-text-secondary">
          Worker payroll roster and Mandor-led gangs. Rates drive payroll;
          gang membership drives harvest attribution.
        </p>
      </div>

      <section className="rounded border border-border-primary">
        <div className="flex items-center justify-between border-b border-border-primary bg-bg-secondary px-3 py-2">
          <div className="font-medium">Workers ({workers.length})</div>
        </div>
        <table className="w-full text-xs">
          <thead className="border-b border-border-primary bg-bg-tertiary text-text-secondary">
            <tr>
              <th className="p-2 text-left">Code</th>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-right">Daily rate (RM)</th>
              <th className="p-2 text-right">Per-bunch (RM)</th>
              <th className="p-2 text-right">Per-kg loose (RM)</th>
              <th className="p-2 text-center">Active</th>
            </tr>
          </thead>
          <tbody>
            {workers.map((w) => (
              <tr key={w.id} className="border-b border-border-primary">
                <td className="p-2 font-mono">{w.code}</td>
                <td className="p-2">{w.name}</td>
                <td className="p-2">{TYPE_LABEL[w.workerType]}</td>
                <td className="p-2 text-right tabular-nums">
                  {w.dailyRateMyr ?? "—"}
                </td>
                <td className="p-2 text-right tabular-nums">
                  {w.perBunchRateMyr ?? "—"}
                </td>
                <td className="p-2 text-right tabular-nums">
                  {w.perLooseFruitKgRate ?? "—"}
                </td>
                <td className="p-2 text-center">
                  {w.isActive ? "✓" : "—"}
                </td>
              </tr>
            ))}
            {!workers.length && (
              <tr>
                <td
                  colSpan={7}
                  className="p-4 text-center text-text-secondary"
                >
                  No workers yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="rounded border border-border-primary">
        <div className="flex items-center justify-between border-b border-border-primary bg-bg-secondary px-3 py-2">
          <div className="font-medium">Gangs ({gangs.length})</div>
        </div>
        <table className="w-full text-xs">
          <thead className="border-b border-border-primary bg-bg-tertiary text-text-secondary">
            <tr>
              <th className="p-2 text-left">Code</th>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Mandor</th>
              <th className="p-2 text-center">Active</th>
            </tr>
          </thead>
          <tbody>
            {gangs.map((g) => (
              <tr key={g.id} className="border-b border-border-primary">
                <td className="p-2 font-mono">{g.code}</td>
                <td className="p-2">{g.name}</td>
                <td className="p-2">
                  {workerById.get(g.mandorWorkerId)?.name ?? "—"}
                </td>
                <td className="p-2 text-center">{g.isActive ? "✓" : "—"}</td>
              </tr>
            ))}
            {!gangs.length && (
              <tr>
                <td
                  colSpan={4}
                  className="p-4 text-center text-text-secondary"
                >
                  No gangs yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
